import React, { useEffect, useState } from 'react';
import {
  useDatasetStatus, useDatasetQuality, usePauseDatasetJob, useResumeDatasetJob, useRunDatasetJob,
  useUpdateDatasetJob, useSetDatasetSession, useRefreshDatasetQuality,
  isBackfillUnavailable,
} from '../hooks/useDataset';
import { SkyboxSessionChip } from './SkyboxSessionPanel';
import AutoLoginsCard from './AutoLoginsCard';
import { formatDateTimeEastern, formatTimeEastern, parseUtcDate, timeAgo } from '../utils/dates';
import {
  num, fmtInt, text, errorMessage, statusSessions, sessionName, backfillIds, sameIdSet,
} from '../utils/datasetStatus';

// Admin → Data (super admin only): the data backbone's one-time backfill
// (every sale since Jan 2025 → its Skybox event, listing, cost and price
// path), the backfill Skybox logins it reads price logs with (each under a
// per-login cap), Lion Auto's Skybox logins (AutoLoginsCard: the unattended
// runner writes prices only through them), and the data-quality report. Every server field may be
// absent or oddly typed (an older engine sends one `session` and no `slots`),
// and before the backfill engine is deployed the status call answers 503: the
// tab then says so and the quality table still works.

// ---- formatting (null-safe; never hands an object to JSX; the shared ones: utils/datasetStatus) ----
const fmtPct = (share, digits = 1) => (num(share) == null ? '—' : `${(num(share) * 100).toFixed(digits)}%`);
const ago = (v) => timeAgo(parseUtcDate(v)) || '—';
// "in 3h 20m" for a future instant ('' when absent or past)
const untilText = (v) => {
  const d = parseUtcDate(v);
  if (!d) return '';
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (mins <= 0) return '';
  if (mins < 60) return `in ${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `in ${h}h ${mins % 60}m`;
  return `in ${Math.floor(h / 24)}d ${h % 24}h`;
};
// 10000 → "10 s", 1500 → "1.5 s"
const windowText = (ms) => (num(ms) == null ? '—' : `${+(num(ms) / 1000).toFixed(1)} s`);

const JOB_INFO = {
  sold_inventory: { label: 'Sold inventory', hint: 'GET /inventory/sold per day → exact links, cost, created, public notes, tags; today+yesterday re-pulled every 15 min' },
  invoice_detail: { label: 'Invoice detail', hint: 'GET /invoices/{id} per sale → exact event + listing link, cost, line ids' },
  event_master: { label: 'Event master', hint: 'GET /events/{id} per event the sales name → events table' },
  listing_history: { label: 'Listing price history', hint: "Each sold / removed listing's full Skybox price log (backfill logins)" },
  activity_event_id: { label: 'Activity event ids', hint: 'Local only: event id on our older price writes' },
  purchases: { label: 'Skybox purchases (per day)', hint: "GET /purchases per UTC day → each purchase's tickets and total for the Reports; today re-pulled every 2 min, yesterday every 15 min, the last 30 days daily" },
  purchase_detail: { label: 'Purchase lines (per purchase)', hint: "GET /purchases/{id} per purchase → its lines: the listing bought, seats and event" },
};
const jobLabel = (job) => JOB_INFO[job]?.label || job;
const DEDICATED_LANE = 'pricing_dedicated';

const anyBackfillLive = (status) => {
  const sessions = statusSessions(status);
  return backfillIds(status).some(id => sessions.some(s => s.userId === id && s.status === 'ok'));
};

const isPaused = (j) => {
  if (typeof j.status === 'string' && j.status.startsWith('paused')) return true;
  const until = parseUtcDate(j.pausedUntil);
  return !!until && until.getTime() > Date.now();
};

// Why the lane last stepped aside for live work (engine extra: skip / skipAt),
// shown only while recent
const recentYield = (j) => {
  const at = parseUtcDate(j.skipAt);
  if (typeof j.skip !== 'string' || !j.skip || !at || Date.now() - at.getTime() > 60000) return '';
  return j.skip.replace(/_/g, ' ');
};

// Status → chip colour (unknown statuses show verbatim, grey)
function statusChip(j) {
  const s = typeof j.status === 'string' ? j.status : '';
  let cls = '';
  if (/auth|error|fail|dead/.test(s)) cls = 'tm-chip-bad';
  else if (s.startsWith('paused') || /wait|yield|backoff/.test(s)) cls = 'tm-chip-warn';
  else if (/run|work|active|busy/.test(s)) cls = 'tm-chip-info';
  else if (/done|complete|caught/.test(s)) cls = 'tm-chip-good';
  const label = s ? s.replace(/_/g, ' ') : (j.enabled ? 'enabled' : 'disabled');
  return <span className={`tm-chip ${cls}`}>{label}</span>;
}

// A run's result, whatever shape the engine returns: its numeric fields
function resultSummary(result) {
  if (num(result) != null) return `: ${fmtInt(result)} items`;
  if (!result || typeof result !== 'object') return '';
  const parts = Object.entries(result).filter(([k, v]) => k !== 'ms' && typeof v === 'number' && Number.isFinite(v)).slice(0, 6)
    .map(([k, v]) => `${k} ${fmtInt(v)}`);
  // backfill.runOnce: why the batch ended (max | nothing_pending | auth | rate_limited | a yield reason)
  const stopped = typeof result.stoppedBy === 'string' && result.stoppedBy ? ` (stopped: ${result.stoppedBy.replace(/_/g, ' ')})` : '';
  return parts.length || stopped ? `: ${parts.join(', ')}${stopped}` : '';
}

function Progress({ done, pending }) {
  const d = num(done) ?? 0;
  const p = num(pending);
  if (p == null) return <span>{fmtInt(d)} done</span>;
  const total = d + p;
  if (!(total > 0)) return <span className="ai-admin-note">—</span>;
  const share = d / total;
  return (
    <div className="ds-progress" title={`${fmtInt(d)} done of ${fmtInt(total)}`}>
      <div className="ds-progress-bar"><div className="ds-progress-fill" style={{ width: `${Math.min(100, share * 100)}%` }} /></div>
      <span className="ds-progress-text">{fmtPct(share)}</span>
    </div>
  );
}

// Editable lane gap (ms between calls); Enter or Set saves, Escape cancels
function GapCell({ gapMs, minGapMs, maxGapMs, busy, onSave }) {
  const [draft, setDraft] = useState(null);
  const current = num(gapMs);
  const editing = draft !== null;
  const parsed = editing && String(draft).trim() !== '' ? Number(draft) : NaN;
  const valid = Number.isInteger(parsed) && parsed >= minGapMs && parsed <= maxGapMs;
  const save = () => {
    if (!editing) return;
    if (!valid) return;
    if (parsed === current) { setDraft(null); return; }
    onSave(parsed, () => setDraft(null));
  };
  return (
    <div className="ds-gap">
      <input
        type="number"
        min={minGapMs}
        max={maxGapMs}
        step="100"
        value={editing ? draft : (current ?? '')}
        placeholder="—"
        className={editing && !valid ? 'ds-input-bad' : ''}
        title={`Milliseconds between calls on this job (${minGapMs}–${maxGapMs}; 1000 = 1 call/s)`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setDraft(null);
        }}
        disabled={busy}
      />
      {editing && (
        <button className="btn-secondary btn-sm" onClick={save} disabled={!valid || busy}>Set</button>
      )}
    </div>
  );
}

// Re-renders every second while `active` (short back-off countdowns between polls)
function useTick(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
}

// The engine's in-flight item for a login, whatever its shape: an item id, an
// { id } object, a count or a flag. '' when nothing is in flight.
function inflightText(v) {
  if (v == null || v === false || v === 0 || v === '') return '';
  if (v === true) return 'a call in flight';
  const id = v && typeof v === 'object' ? (v.id ?? v.itemId ?? v.item_id) : v;
  if (typeof id === 'number' && id < 1000) return `${fmtInt(id)} call${id === 1 ? '' : 's'} in flight`;
  return id != null && id !== '' && typeof id !== 'object' ? `reading listing ${text(id, 40)}` : 'a call in flight';
}

// Seconds until a login's back-off ends (0 when none or past)
const backoffLeftSec = (v) => {
  const d = parseUtcDate(v);
  return d ? Math.max(0, Math.ceil((d.getTime() - Date.now()) / 1000)) : 0;
};

// One saved backfill login: its session, its calls against its cap, back-off,
// what it is reading. `slot` = the engine's view (absent on an older engine).
function SlotRow({ id, slot, session }) {
  const s = slot || {};
  const live = (session?.status ?? s.status) === 'ok';
  const limit = num(s.limit);
  const effective = num(s.effectiveLimit) ?? limit;
  const backoffSec = backoffLeftSec(s.backoffUntil);
  // Longer than two windows = a 403 hold (30 min), not a 429 back-off
  const held = backoffSec > 2 * ((num(s.windowMs) ?? 10000) / 1000);
  const busyText = inflightText(s.inflight);
  return (
    <div className="ds-slot-row">
      <div className="ds-login-current">
        <b>{sessionName(session || s, id)}</b>
        <SkyboxSessionChip session={session || s} />
        {effective != null ? (
          <span className="ds-num" title="Calls in the current window / the login's cap now">
            {fmtInt(s.callsInWindow)} / {fmtInt(effective)} per {windowText(s.windowMs)}
            {limit != null && effective < limit ? ` (cap ${fmtInt(limit)}, cut after a 429)` : ''}
          </span>
        ) : (
          <span className="ds-num">{fmtInt(session?.callsLast10s ?? s.callsLast10s)} calls in the last 10 s</span>
        )}
        {backoffSec > 0 && (
          <span className="tm-chip tm-chip-warn">
            {held ? `paused until ${formatTimeEastern(s.backoffUntil)} (Skybox refused this login, 403)` : `429 back-off ${backoffSec} s`}
          </span>
        )}
        {busyText && <span className="ai-admin-note">{busyText}</span>}
        {num(s.callsSinceBoot) != null && <span className="ai-admin-note">{fmtInt(s.callsSinceBoot)} calls since restart</span>}
      </div>
      {!live && (
        <div className="tm-warn" style={{ marginTop: 4 }}>
          Not live — paste fresh tokens for {sessionName(session || s, id)} in Admin → Users. The other backfill logins keep going.
        </div>
      )}
      {text(s.lastError) && <div className="ai-admin-note tm-error-text">Last error: {text(s.lastError)}</div>}
    </div>
  );
}

// Calls per window on each backfill login (the dedicated-lane job's slotCap)
function SlotCapEditor({ status, onMessage }) {
  const update = useUpdateDatasetJob();
  const [draft, setDraft] = useState(null); // null = untouched: show the saved cap
  const cap = status?.slotCap && typeof status.slotCap === 'object' ? status.slotCap : null;
  const b = status?.slotCapBounds && typeof status.slotCapBounds === 'object' ? status.slotCapBounds : {};
  const minLimit = num(b.minLimit) ?? 1;
  const maxLimit = num(b.maxLimit) ?? 60;
  const minWindowMs = num(b.minWindowMs) ?? 1000;
  const maxWindowMs = num(b.maxWindowMs) ?? 10000;
  const recover = num(status?.cleanCallsToRecover) ?? 50;
  const jobs = Array.isArray(status?.jobs) ? status.jobs : [];
  const job = jobs.find(j => j && j.lane === DEDICATED_LANE)?.job || 'listing_history';
  const note = `A 429 pauses that login for one window and halves its cap; every ${fmtInt(recover)} clean calls double it back toward the cap. Pricers' logins are never used.`;

  if (!cap) {
    return <div className="ai-admin-note" style={{ marginTop: 8 }}>Per-login cap: fixed on this server (12 per 10 s). {note}</div>;
  }
  const cur = { limit: num(cap.limit), windowMs: num(cap.windowMs) };
  const d = draft || { limit: cur.limit ?? '', windowMs: cur.windowMs ?? '' };
  const limit = String(d.limit).trim() === '' ? NaN : Number(d.limit);
  const windowMs = String(d.windowMs).trim() === '' ? NaN : Number(d.windowMs);
  const valid = Number.isInteger(limit) && limit >= minLimit && limit <= maxLimit
    && Number.isInteger(windowMs) && windowMs >= minWindowMs && windowMs <= maxWindowMs;
  const dirty = draft !== null && (limit !== cur.limit || windowMs !== cur.windowMs);
  const save = () => {
    if (!valid || !dirty || update.isPending) return;
    update.mutate({ job, slotCap: { limit, windowMs } }, {
      onSuccess: () => { setDraft(null); onMessage?.(`Per-login cap: ${limit} calls per ${windowText(windowMs)}`); },
      onError: (e) => onMessage?.(errorMessage(e, 'Failed to set the per-login cap'), true),
    });
  };
  const onKey = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setDraft(null);
  };
  return (
    <div style={{ marginTop: 10 }}>
      <div className="ai-admin-row ds-cap-row">
        <span>Per-login cap:</span>
        <input
          type="number" min={minLimit} max={maxLimit} step="1" value={d.limit}
          className={draft && !valid ? 'ds-input-bad' : ''}
          title={`Calls per window on each backfill login (${minLimit}–${maxLimit})`}
          onChange={(e) => setDraft({ ...d, limit: e.target.value })} onKeyDown={onKey} disabled={update.isPending}
        />
        <span>calls per</span>
        <input
          type="number" min={minWindowMs} max={maxWindowMs} step="500" value={d.windowMs}
          className={draft && !valid ? 'ds-input-bad' : ''}
          title={`Window length in milliseconds (${minWindowMs}–${maxWindowMs}; 1000 = 1 s)`}
          onChange={(e) => setDraft({ ...d, windowMs: e.target.value })} onKeyDown={onKey} disabled={update.isPending}
        />
        <span>ms</span>
        <button className="btn-secondary btn-sm" onClick={save} disabled={!valid || !dirty || update.isPending}>
          {update.isPending ? 'Setting…' : 'Set'}
        </button>
        {valid && <span className="ai-admin-note">≈ {+(limit * 1000 / windowMs).toFixed(2)} calls/s per login</span>}
      </div>
      <div className="ai-admin-note">{note}</div>
    </div>
  );
}

function BackfillLoginsCard({ status, unavailable, loadError, onMessage }) {
  const setSession = useSetDatasetSession();
  const [picks, setPicks] = useState(null); // null = untouched (show the saved ones)
  const sessions = statusSessions(status);
  const currentIds = backfillIds(status);
  const selected = picks ?? currentIds;
  const dirty = !sameIdSet(selected, currentIds);
  const pickedActive = sessions.filter(s => selected.includes(s.userId) && s.userActive === true);
  const slots = Array.isArray(status?.slots) ? status.slots.filter(s => s && typeof s === 'object') : [];
  const rowIds = [...currentIds, ...slots.map(s => s.userId).filter(id => Number.isInteger(id) && !currentIds.includes(id))];
  useTick(slots.some(s => { const sec = backoffLeftSec(s.backoffUntil); return sec > 0 && sec <= 120; }));

  const toggle = (id, on) => setPicks(on ? [...selected.filter(x => x !== id), id] : selected.filter(x => x !== id));
  const save = () => {
    setSession.mutate(selected, {
      onSuccess: () => {
        setPicks(null);
        onMessage?.(selected.length ? `Backfill logins saved: ${selected.length}` : 'Backfill logins cleared');
      },
      onError: (err) => onMessage?.(errorMessage(err, 'Failed to save the backfill logins'), true),
    });
  };

  return (
    <div className="ai-admin-card ds-login-card">
      <h4>Backfill Skybox logins</h4>
      {unavailable ? (
        <div className="ai-admin-note">Backfill engine not running on this server — nothing to pick yet.</div>
      ) : loadError ? (
        <div className="ai-admin-note tm-error-text">{loadError}</div>
      ) : !status ? (
        <div className="ai-admin-note">Loading…</div>
      ) : (
        <>
          {sessions.length === 0 ? (
            <div className="ai-admin-note">No user has a Skybox session yet.</div>
          ) : (
            <div className="ds-login-list">
              {sessions.map(s => {
                const checked = selected.includes(s.userId);
                // Lion Auto's logins are never borrowed (the server refuses: SLOT_IN_USE)
                const isAuto = s.purpose === 'auto';
                const blocked = s.userActive === true || isAuto;
                return (
                  <label key={s.userId} className={blocked ? 'ds-login-blocked' : ''}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggle(s.userId, e.target.checked)}
                      disabled={setSession.isPending || (blocked && !checked)}
                    />
                    <span>{sessionName(s)}</span>
                    {s.username && s.displayName && s.username !== s.displayName && <span className="ai-admin-note">{text(s.username)}</span>}
                    <SkyboxSessionChip session={s} />
                    {s.userActive === true && <span className="tm-chip tm-chip-warn">active user — not allowed</span>}
                    {s.userActive === false && <span className="tm-chip">inactive user</span>}
                    {isAuto && <span className="tm-chip tm-chip-info">Lion Auto login</span>}
                  </label>
                );
              })}
            </div>
          )}
          <div className="ai-admin-row">
            <button className="btn-primary btn-sm" onClick={save} disabled={!dirty || setSession.isPending || pickedActive.length > 0}>
              {setSession.isPending ? 'Saving…' : 'Save'}
            </button>
            {dirty && !setSession.isPending && (
              <button className="btn-secondary btn-sm" onClick={() => setPicks(null)}>Undo</button>
            )}
            <span className="ai-admin-note">{fmtInt(selected.length)} picked</span>
          </div>
          {sessions.some(s => s.userActive === true) && (
            <div className="tm-warn">Users that can sign in to the portal can't be backfill logins: their Skybox budget would be shared with a pricer. Make them inactive in Admin → Users first.</div>
          )}
          {rowIds.length === 0 ? (
            <div className="ai-admin-note" style={{ marginTop: 6 }}>None saved — the price-history job waits for one.</div>
          ) : (
            <div className="ds-slot-list">
              {rowIds.map(id => (
                <SlotRow key={id} id={id} slot={slots.find(s => s.userId === id)} session={sessions.find(s => s.userId === id)} />
              ))}
            </div>
          )}
          <SlotCapEditor status={status} onMessage={onMessage} />
          <div className="ai-admin-note" style={{ marginTop: 8 }}>Add more backfill logins (any number; more = faster):</div>
          <ol className="ds-steps">
            <li>Admin → Users: create <code>skybox-backfill-N</code> (role pricer) as an <b>inactive</b> user.</li>
            <li>Paste a different Skybox login's token pair into it (a login used for nothing else).</li>
            <li>Tick every backfill login here → <b>Save</b>. Each reads on its own Skybox budget.</li>
          </ol>
        </>
      )}
    </div>
  );
}

function JobsTable({ status, onMessage }) {
  const pause = usePauseDatasetJob();
  const resume = useResumeDatasetJob();
  const run = useRunDatasetJob();
  const update = useUpdateDatasetJob();

  const jobs = Array.isArray(status?.jobs) ? status.jobs.filter(j => j && typeof j === 'object' && typeof j.job === 'string') : [];
  const minGapMs = num(status?.minGapMs) ?? 500;
  const maxGapMs = num(status?.maxGapMs) ?? 60000;
  const runMax = num(status?.runMax) ?? 50;
  const manualRuns = status?.manualRuns && typeof status.manualRuns === 'object' ? status.manualRuns : {};
  const dedicatedLive = anyBackfillLive(status);
  const busy = (m, job) => m.isPending && (m.variables === job || m.variables?.job === job);

  const fail = (err, fallback) => onMessage?.(errorMessage(err, fallback), true);
  const doPause = (job) => pause.mutate(job, { onSuccess: () => onMessage?.(`${jobLabel(job)} paused`), onError: (e) => fail(e, 'Pause failed') });
  const doResume = (job) => resume.mutate(job, { onSuccess: () => onMessage?.(`${jobLabel(job)} resumed`), onError: (e) => fail(e, 'Resume failed') });
  const doRun = (job) => run.mutate(job, {
    onSuccess: (res) => {
      if (res?.status === 202) onMessage?.(`${jobLabel(job)}: run started — up to ${runMax} items continue in the background`);
      else onMessage?.(`${jobLabel(job)}: run finished${resultSummary(res?.data?.result)}`);
    },
    onError: (e) => fail(e, 'Run failed'),
  });
  const doToggle = (j, enabled) => {
    if (enabled && j.lane === DEDICATED_LANE && !dedicatedLive
      && !window.confirm(`${jobLabel(j.job)} reads with the backfill Skybox logins, and none of them is live yet. It will wait until one is. Enable anyway?`)) return;
    update.mutate({ job: j.job, enabled }, {
      onSuccess: () => onMessage?.(`${jobLabel(j.job)} ${enabled ? 'enabled' : 'disabled'}`),
      onError: (e) => fail(e, 'Update failed'),
    });
  };
  const doGap = (job, gapMs, done) => update.mutate({ job, gapMs }, {
    onSuccess: () => { done(); onMessage?.(`${jobLabel(job)}: ${gapMs} ms between calls`); },
    onError: (e) => fail(e, 'Update failed'),
  });

  if (!jobs.length) return <div className="ai-admin-note">The engine reports no jobs.</div>;

  return (
    <div className="table-container ds-jobs">
      <table>
        <thead>
          <tr>
            <th>Job</th><th>Lane</th><th>Status</th><th>On</th><th>Progress</th><th>Done</th><th>Pending</th>
            <th>Gone</th><th>Errors</th><th>Rate / min</th><th>ETA</th><th>Gap (ms)</th><th></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(j => {
            const paused = isPaused(j);
            const pausedUntil = untilText(j.pausedUntil);
            const pending = num(j.pending);
            const eta = parseUtcDate(j.etaAt);
            const lastError = text(j.lastError);
            const manualRunAt = typeof manualRuns[j.job] === 'string' ? manualRuns[j.job] : null;
            const rowBusy = busy(pause, j.job) || busy(resume, j.job) || busy(update, j.job);
            return (
              <React.Fragment key={j.job}>
                <tr className={lastError ? 'ds-has-error' : ''}>
                  <td>
                    <div className="ds-job-name" title={JOB_INFO[j.job]?.hint || ''}>{jobLabel(j.job)}</div>
                    <div className="ai-admin-note">{j.job}{j.lastItem != null && j.lastItem !== '' ? ` · last ${text(j.lastItem, 40)}` : ''}</div>
                  </td>
                  <td>
                    {text(j.lane) || '—'}
                    {j.lane === DEDICATED_LANE && !dedicatedLive && <span className="tm-chip tm-chip-warn">needs login</span>}
                  </td>
                  <td>
                    {statusChip(j)}
                    {pausedUntil && <div className="ai-admin-note">resumes {pausedUntil}</div>}
                    {recentYield(j) && <div className="ai-admin-note">yielding: {recentYield(j)}</div>}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!j.enabled}
                      onChange={(e) => doToggle(j, e.target.checked)}
                      disabled={busy(update, j.job)}
                      title={j.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                    />
                  </td>
                  <td><Progress done={j.done} pending={j.pending} /></td>
                  <td>{fmtInt(j.done)}</td>
                  <td>{fmtInt(j.pending)}</td>
                  <td>{fmtInt(j.gone)}</td>
                  <td className={num(j.errors) > 0 ? 'tm-error-text' : ''}>{fmtInt(j.errors)}</td>
                  <td>{num(j.ratePerMin) == null ? '—' : (Math.round(num(j.ratePerMin) * 10) / 10).toLocaleString('en-US')}</td>
                  <td className="ds-eta">
                    {pending === 0 ? 'done' : eta ? (
                      <>
                        {formatDateTimeEastern(eta)}
                        {untilText(eta) && <div className="ai-admin-note">{untilText(eta)}</div>}
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    {j.lane === DEDICATED_LANE ? (
                      <span className="ai-admin-note" title="Paced per login: set the cap under Backfill Skybox logins above">per-login cap</span>
                    ) : (
                      <GapCell gapMs={j.gapMs} minGapMs={minGapMs} maxGapMs={maxGapMs} busy={busy(update, j.job)} onSave={(ms, done) => doGap(j.job, ms, done)} />
                    )}
                  </td>
                  <td className="ds-actions">
                    {paused ? (
                      <button className="btn-secondary btn-sm" onClick={() => doResume(j.job)} disabled={rowBusy}>Resume</button>
                    ) : (
                      <button className="btn-secondary btn-sm" onClick={() => doPause(j.job)} disabled={rowBusy}>Pause</button>
                    )}
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => doRun(j.job)}
                      disabled={busy(run, j.job) || !!manualRunAt}
                      title={manualRunAt ? `Run in progress since ${formatDateTimeEastern(manualRunAt)}` : `Process up to ${runMax} items now`}
                    >
                      {busy(run, j.job) || manualRunAt ? 'Running…' : `Run ${runMax}`}
                    </button>
                  </td>
                </tr>
                {lastError && (
                  <tr className="ds-error-row">
                    <td colSpan={13}>
                      Last error{parseUtcDate(j.lastErrorAt) ? ` (${ago(j.lastErrorAt)})` : ''}: {lastError}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const targetText = (t) => {
  if (!t || typeof t !== 'object') return 'report';
  if (num(t.min) != null) return `≥ ${+(num(t.min) * 100).toFixed(2)}%`;
  if (num(t.max) != null) return `< ${+(num(t.max) * 100).toFixed(2)}%`;
  return 'report';
};

function resultChip(m) {
  if (m.pass === true) return <span className="tm-chip tm-chip-good">pass</span>;
  if (m.pass === false) return <span className="tm-chip tm-chip-bad">fail</span>;
  return <span className="tm-chip">{m.threshold ? 'no data' : 'report'}</span>;
}

// { link: n } → "detail 120, identity 4"
const tallyText = (o) => {
  if (!o || typeof o !== 'object') return '—';
  const parts = Object.entries(o).filter(([, v]) => num(v) != null).sort((a, b) => num(b[1]) - num(a[1]))
    .map(([k, v]) => `${k === 'null' ? 'none' : k} ${fmtInt(v)}`);
  return parts.length ? parts.join(', ') : '—';
};

// The report's markups rows (marketplace × month) → each marketplace's latest
// month, most sales first: "StubHub ×1.10 (n 120, 2026-09)". '' when absent.
// These are exchange markups on B ("List $X" ÷ B), never an L.
function markupsText(rows) {
  if (!Array.isArray(rows)) return '';
  const latest = new Map();
  for (const r of rows) {
    if (!r || typeof r !== 'object' || num(r.ratio) == null) continue;
    const key = text(r.marketplace, 60) || '(none)';
    const month = text(r.month, 20);
    const cur = latest.get(key);
    if (!cur || month > cur.month) latest.set(key, { month, ratio: num(r.ratio), n: num(r.n) ?? 0 });
  }
  return [...latest.entries()].sort((a, b) => b[1].n - a[1].n)
    .map(([k, v]) => `${k} ×${v.ratio.toFixed(2)} (n ${fmtInt(v.n)}${v.month ? `, ${v.month}` : ''})`).join(' · ');
}

function QualitySection({ onMessage }) {
  const { data, isLoading, error } = useDatasetQuality();
  const refresh = useRefreshDatasetQuality();
  const snap = data?.snapshot && typeof data.snapshot === 'object' ? data.snapshot : null;
  const metrics = Array.isArray(snap?.metrics) ? snap.metrics.filter(m => m && typeof m === 'object') : [];
  const errors = snap?.errors && typeof snap.errors === 'object' ? Object.entries(snap.errors) : [];
  const items = snap?.items && typeof snap.items === 'object' ? Object.entries(snap.items) : [];
  const markups = markupsText(snap?.markups);

  const doRefresh = () => refresh.mutate(undefined, {
    onSuccess: () => onMessage?.('Data-quality report refreshed'),
    onError: (err) => onMessage?.(errorMessage(err, err?.response?.status === 429 ? 'Refreshed less than a minute ago — try again shortly' : 'Refresh failed'), true),
  });

  return (
    <>
      <div className="ds-section-head">
        <h3>Data quality</h3>
        <span className="ai-admin-note">
          {snap?.computedAt ? `Computed ${formatDateTimeEastern(snap.computedAt)} (${ago(snap.computedAt)})` : 'Not computed yet'}
          {snap?.scope?.from ? ` · sales since ${text(snap.scope.from)}` : ''}
          {num(snap?.ms) != null ? ` · ${fmtInt(snap.ms)} ms` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <button className="btn-secondary btn-sm" onClick={doRefresh} disabled={refresh.isPending} title="Recompute the report now (at most once a minute; it also runs nightly)">
          {refresh.isPending ? 'Computing…' : 'Refresh'}
        </button>
      </div>
      {isLoading ? (
        <div className="ai-admin-note">Loading…</div>
      ) : error ? (
        <div className="ai-admin-note tm-error-text">{errorMessage(error, 'Failed to load the data-quality report')}</div>
      ) : !snap ? (
        <div className="ai-admin-note">No report stored yet — it is computed nightly, or press Refresh.</div>
      ) : (
        <>
          <div className="table-container ds-quality">
            <table>
              <thead>
                <tr><th>Metric</th><th>Value</th><th>Count</th><th>Target</th><th>Result</th></tr>
              </thead>
              <tbody>
                {metrics.map((m, i) => {
                  const isCount = m.denominator == null;
                  return (
                    <tr key={typeof m.key === 'string' ? m.key : i}>
                      <td>
                        {text(m.label) || text(m.key)}
                        {m.note ? <div className="ai-admin-note">{text(m.note)}</div> : null}
                      </td>
                      <td className="ds-num">{isCount ? fmtInt(m.value) : fmtPct(m.value)}</td>
                      <td className="ds-num">{isCount ? fmtInt(m.numerator ?? m.value) : `${fmtInt(m.numerator)} / ${fmtInt(m.denominator)}`}</td>
                      <td>{targetText(m.threshold)}</td>
                      <td>{resultChip(m)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ai-admin-note" style={{ marginTop: 6 }}>
            Event links: {tallyText(snap.links?.event)} · Listing links: {tallyText(snap.links?.listing)}
          </div>
          {markups && (
            <div className="ai-admin-note" title="List $X ÷ the last B before the sale, modal per marketplace and month (an exchange's markup on B, not an L)">
              Marketplace markups (latest month): {markups}
            </div>
          )}
          {items.length > 0 && (
            <div className="ai-admin-note">
              Backfill items (by status): {items.map(([job, byStatus]) => `${job} (${tallyText(byStatus)})`).join(' · ')}
            </div>
          )}
          {errors.length > 0 && (
            <div className="ai-admin-note tm-error-text">
              Not measured: {errors.map(([k, v]) => `${k} (${text(v, 120)})`).join(' · ')}
            </div>
          )}
        </>
      )}
    </>
  );
}

function DataAdminTab({ onMessage }) {
  const { data, error, isLoading, dataUpdatedAt, refetch, isFetching } = useDatasetStatus();
  const unavailable = isBackfillUnavailable(error);
  // A failed poll keeps the last good status on screen (React Query keeps data)
  const status = data && typeof data === 'object' ? data : null;
  const loadError = error && !unavailable && !status ? errorMessage(error, 'Failed to load the backfill status') : null;

  return (
    <div className="card">
      <div className="card-header">
        <h2>Data backbone</h2>
        <span className="ai-admin-note">
          {status && dataUpdatedAt ? `Updated ${timeAgo(dataUpdatedAt)} · refreshes every 10 s` : ''}
        </span>
      </div>
      <div className="ai-admin-note" style={{ marginBottom: 12 }}>
        One-time backfill from Skybox: every sale since Jan 2025 gets its event, listing, cost and price path; afterwards new sales are kept linked automatically. Jobs run newest first, at a steady pace, and yield to live work.
      </div>

      <div className="ai-admin-grid">
        <BackfillLoginsCard status={status} unavailable={unavailable} loadError={loadError} onMessage={onMessage} />
        <AutoLoginsCard status={status} unavailable={unavailable} loadError={loadError} onMessage={onMessage} />
      </div>

      <h3>Backfill jobs</h3>
      {unavailable ? (
        <div className="ai-admin-row">
          <span className="ai-admin-note">Backfill engine not running on this server.</span>
          <button className="btn-secondary btn-sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Checking…' : 'Check again'}
          </button>
        </div>
      ) : loadError ? (
        <div className="ai-admin-note tm-error-text">{loadError}</div>
      ) : isLoading || !status ? (
        <div className="ai-admin-note">Loading…</div>
      ) : (
        <>
          <JobsTable status={status} onMessage={onMessage} />
          <div className="ai-admin-note" style={{ marginTop: 6 }}>
            Suggested order: Sold inventory first (Run {fmtInt(num(status?.runMax) ?? 50)} once, then On); Event master; Listing price history once the backfill logins are live; Invoice detail stays on for leftovers. Gap = milliseconds between calls on that job's lane (1000 = 1 call/s); a 429 from Skybox doubles it. Listing price history is paced per login instead (cap above).
          </div>
        </>
      )}

      <QualitySection onMessage={onMessage} />
    </div>
  );
}

export default DataAdminTab;
